import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Settings, Shield, FileText, Info, LogOut, ChevronRight, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const APP_VERSION = '2.0.0';

const AccountSettings = () => {
  const { logout, token } = useAuth();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast({
      title: 'Logged out',
      description: "You've been successfully logged out."
    });
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Account deleted', description: 'Your account and all data have been permanently removed.' });
      logout();
      navigate('/');
    } catch (error) {
      toast({ title: 'Failed to delete account', description: error.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const settingsItems = [
    {
      icon: Info,
      title: 'App Version',
      description: `Version ${APP_VERSION}`,
      action: null
    },
    {
      icon: FileText,
      title: 'Terms & Conditions',
      description: 'Read our terms of service',
      action: () => window.open('/terms', '_blank')
    },
    {
      icon: Shield,
      title: 'Privacy Policy',
      description: 'How we handle your data',
      action: () => window.open('/privacy', '_blank')
    }
  ];

  return (
    <div className="space-y-6" data-testid="account-settings">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Account Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {settingsItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                onClick={item.action}
                className={`flex items-center justify-between p-4 rounded-lg border border-gray-200 ${
                  item.action ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''
                }`}
                data-testid={`settings-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Icon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{item.title}</p>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                </div>
                {item.action && <ChevronRight className="h-5 w-5 text-gray-400" />}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Logout */}
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-red-500 text-red-500 hover:bg-red-50"
            data-testid="logout-btn-settings"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Log Out
          </Button>
        </CardContent>
      </Card>

      {/* Delete Account */}
      <Card className="border-red-300">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-red-600">Danger Zone</h3>
              <p className="text-xs text-gray-500 mt-1">Permanently delete your account and all data</p>
            </div>
          </div>
          <Button
            variant="destructive"
            className="w-full"
            onClick={() => setShowDeleteDialog(true)}
            data-testid="delete-account-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete Account
          </Button>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Account
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This will permanently delete your account, all matches, posts, diary entries, messages, AI coaching sessions, and friend connections. This action <strong>cannot be undone</strong>.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteAccount} disabled={deleting} data-testid="confirm-delete-account-btn">
              {deleting ? 'Deleting...' : 'Delete Everything'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountSettings;
